/**
 * 附件校验（T09）。
 *
 * 这里集中定义「允许的类型清单」与「大小 / 数量上限」，并提供可复用的校验 / 格式化函数，
 * 后续调整只需改本文件。校验只依赖文件的最小形状（name / type / size），
 * 因此浏览器 File 与测试里的构造对象都能直接使用。
 */

import type { Attachment } from '../../core/types';

/** 单次选择的附件数量上限。*/
export const MAX_ATTACHMENT_COUNT = 5;

/** 单文件大小上限：图片 10MB，其它（文件 / 音频）20MB。*/
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_OTHER_BYTES = 20 * 1024 * 1024;

/** dataUrl 超过该阈值时，持久化前给出明确提示（本轮不做压缩）。*/
export const DATA_URL_WARN_BYTES = 4 * 1024 * 1024;

/** 允许的图片 MIME。*/
export const ALLOWED_IMAGE_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/svg+xml',
] as const;

/** 允许的音频 MIME。*/
export const ALLOWED_AUDIO_MIME = [
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'audio/mp4',
  'audio/aac',
] as const;

/** 允许的常见文件 MIME（文档 / 表格 / 演示 / 压缩包 / 纯文本）。*/
export const ALLOWED_FILE_MIME = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
  'application/zip',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

/** 允许的类型清单（唯一事实来源）。*/
export const ALLOWED_MIME_TYPES: readonly string[] = [
  ...ALLOWED_IMAGE_MIME,
  ...ALLOWED_AUDIO_MIME,
  ...ALLOWED_FILE_MIME,
];

/** 隐藏 <input type="file"> 的 accept：图片 + 音频 + 常见文件（含扩展名兜底）。*/
export const ATTACHMENT_ACCEPT = [
  'image/*',
  'audio/*',
  '.pdf',
  '.txt',
  '.md',
  '.csv',
  '.json',
  '.zip',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
].join(',');

/** 校验所需的最小文件形状（File 天然满足）。*/
export interface FileLike {
  name: string;
  type: string;
  size: number;
}

/** 单个文件的拒绝记录；reason 为可直接展示给用户的中文提示。*/
export interface FileRejection {
  file: FileLike;
  reason: string;
}

export interface BatchValidation<T extends FileLike = FileLike> {
  accepted: T[];
  rejected: FileRejection[];
}

/** MIME → 附件类型：image/* → image，audio/* → audio，其余 → file。*/
export function inferKind(mime: string): Attachment['kind'] {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

/** 该类型对应的单文件大小上限。*/
export function maxBytesForKind(kind: Attachment['kind']): number {
  return kind === 'image' ? MAX_IMAGE_BYTES : MAX_OTHER_BYTES;
}

/** 字节数 → 便于阅读的中文大小文案。*/
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '未知大小';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${round1(kb)} KB`;
  return `${round1(kb / 1024)} MB`;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * 校验单个文件。
 *
 * @returns 通过返回 null；否则返回中文拒绝原因。
 */
export function validateFile(file: FileLike): string | null {
  if (file.type !== '' && !ALLOWED_MIME_TYPES.includes(file.type)) {
    return `不支持的类型：${file.name}`;
  }
  const limit = maxBytesForKind(inferKind(file.type));
  if (file.size > limit) {
    return `${file.name} 超过 ${formatFileSize(limit)} 上限（当前 ${formatFileSize(file.size)}）`;
  }
  return null;
}

/**
 * 批量校验：逐个判断，超限文件单独拒绝并给出原因，不影响同批其它合法文件。
 *
 * 数量上限按「已选数量 + 本次合法文件」计算；被拒绝的文件不占用名额。
 *
 * @param files 本次新选的文件。
 * @param existingCount 已选中的数量，用于数量上限。
 */
export function validateFiles<T extends FileLike>(
  files: readonly T[],
  existingCount = 0,
): BatchValidation<T> {
  const accepted: T[] = [];
  const rejected: FileRejection[] = [];
  let remaining = Math.max(0, MAX_ATTACHMENT_COUNT - Math.max(0, existingCount));

  for (const file of files) {
    if (remaining <= 0) {
      rejected.push({
        file,
        reason: `最多只能添加 ${MAX_ATTACHMENT_COUNT} 个附件，已忽略：${file.name}`,
      });
      continue;
    }
    const reason = validateFile(file);
    if (reason !== null) {
      rejected.push({ file, reason });
      continue;
    }
    accepted.push(file);
    remaining -= 1;
  }

  return { accepted, rejected };
}

/** 是否存在超过提示阈值的 dataUrl（持久化前用于给出明确警告）。*/
export function hasOversizedDataUrl(
  attachments: readonly Attachment[],
  warnBytes: number = DATA_URL_WARN_BYTES,
): boolean {
  return attachments.some((item) => (item.dataUrl?.length ?? 0) > warnBytes);
}